/**
 * Code promo pour la page /a/[slug] (adhésion) :
 * apply / remove + discount calculé sur le subtotal.
 */
import { membershipsApi } from "~/services/api/memberships";

export function useMembershipPromoCode(slug: Ref<string>, subtotal: Ref<number>) {
  const { t } = useI18n();
  const promoCode = ref("");
  const promoLoading = ref(false);
  const promoError = ref("");
  const promoApplied = ref<{ code: string; discount_type: string; discount_value: number } | null>(null);

  async function applyPromoCode() {
    if (!promoCode.value.trim() || promoLoading.value) return;
    promoLoading.value = true;
    promoError.value = "";
    try {
      const result = await membershipsApi.validatePromo(slug.value, promoCode.value.trim().toUpperCase());
      if (result.valid) {
        promoApplied.value = {
          code: promoCode.value.trim().toUpperCase(),
          discount_type: result.discount_type!,
          // L'API renvoie la valeur en texte décimal (« 10.00000 ») : en nombre,
          // comme la billetterie et la boutique, sinon le récap affichait
          // « -10.00000% » (2026-10-19).
          discount_value: Number(result.discount_value || 0),
        };
      } else {
        promoError.value = result.message || t("adhesion.useMembershipPromoCode.codePromoInvalide");
      }
    } catch (e: unknown) {
      promoError.value = (e as Error)?.message || t("adhesion.useMembershipPromoCode.codePromoInvalide");
    } finally {
      promoLoading.value = false;
    }
  }

  function removePromo() {
    promoApplied.value = null;
    promoCode.value = "";
    promoError.value = "";
  }

  const promoDiscount = computed(() => {
    if (!promoApplied.value) return 0;
    const base = subtotal.value;
    if (promoApplied.value.discount_type === "percentage") {
      return Math.round((base * promoApplied.value.discount_value / 100) * 100) / 100;
    }
    return Math.min(promoApplied.value.discount_value, base);
  });

  return { promoCode, promoLoading, promoError, promoApplied, promoDiscount, applyPromoCode, removePromo };
}
